import { useEffect, useState } from "react";
import { useLeagues } from "../context/leagues";
import { useUser } from "../context/user";
import "./booth/Modal.css";
import "../styles/leagueInfo.css";

const PLATFORM_LABEL = {
  espn: "ESPN",
  sleeper: "Sleeper",
};

function isReceiving(member) {
  return !!(member.members?.email && member.members?.subscribed);
}

function EditIcon() {
  return (
    <svg
      data-testid="EditIcon"
      className="booth-league-info-edit-glyph"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LeagueInfo({ open, league, onClose }) {
  const leagues = useLeagues();
  const user = useUser();
  const [editingId, setEditingId] = useState(null);
  const [draftEmail, setDraftEmail] = useState("");
  const [draftSubscribed, setDraftSubscribed] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    let cancelled = false;
    leagues.getInviteLink(league.id).then((token) => {
      if (!cancelled && token) {
        setInviteLink(`${window.location.origin}/join/${token}`);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league.id]);

  const handleCopyInviteLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setInviteLinkCopied(true);
    setTimeout(() => setInviteLinkCopied(false), 2000);
  };

  const members = league.league_members;
  const totalCount = members.length;
  const receivingCount = members.filter(isReceiving).length;
  const needsAttentionCount = totalCount - receivingCount;
  const platformLabel = PLATFORM_LABEL[league.service] ?? null;

  const startEditing = (member) => {
    setEditingId(member.members.id);
    setDraftEmail(member.members.email || "");
    setDraftSubscribed(!!member.members.subscribed);
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const saveEditing = async (memberId) => {
    await leagues.updateLeague(
      league.id,
      { [memberId]: draftEmail },
      { [memberId]: draftSubscribed }
    );
    setEditingId(null);
  };

  return (
    <div
      className={`booth-modal-overlay ${open ? "booth-modal-overlay--open" : ""}`}
      onClick={handleOverlayClick}
      aria-hidden={!open}
    >
      <div
        className="booth-modal-panel booth-league-info-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Manage ${league.name}`}
      >
        <button
          type="button"
          className="booth-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <h2 className="booth-account-heading">{league.name}</h2>
        {platformLabel && (
          <span className="booth-league-info-platform">{platformLabel}</span>
        )}

        <div className="booth-league-info-coverage">
          <span className="booth-league-info-coverage-stat">
            <span className="booth-league-info-coverage-num">
              {receivingCount}
            </span>
            <span className="booth-league-info-coverage-total">
              / {totalCount}
            </span>
            <span className="booth-league-info-coverage-label">
              managers receive the dispatch
            </span>
          </span>
          <div
            role="img"
            aria-label={`${receivingCount} of ${totalCount} managers receiving`}
            className="booth-league-info-coverage-bar"
          >
            {members.map((member, index) => (
              <span
                key={member.members.id}
                className={`booth-league-info-coverage-seg ${
                  index < receivingCount
                    ? "booth-league-info-coverage-seg--filled"
                    : ""
                }`}
              />
            ))}
          </div>
        </div>

        <div className="booth-field booth-league-info-invite">
          <label className="booth-field-label" htmlFor="invite-link-input">
            Invite link
          </label>
          <div className="booth-league-info-invite-row">
            <input
              id="invite-link-input"
              type="text"
              readOnly
              className="booth-field-input"
              value={inviteLink || ""}
              aria-describedby="invite-link-help"
            />
            <button
              type="button"
              className="booth-btn booth-btn--primary booth-btn--sm"
              onClick={handleCopyInviteLink}
            >
              {inviteLinkCopied ? "Copied!" : "Copy link"}
            </button>
          </div>
          <span id="invite-link-help" className="booth-league-info-help">
            Anyone with this link can add their own email.
          </span>
        </div>

        <section aria-label="Managers" className="booth-league-info-managers">
          <div className="booth-league-info-managers-header">
            <span className="booth-league-info-managers-title">Managers</span>
            <div className="booth-league-info-managers-summary">
              <span className="booth-league-info-managers-summary-all">
                All {totalCount}
              </span>
              <span>Receiving {receivingCount}</span>
              <span>Needs attention {needsAttentionCount}</span>
            </div>
          </div>

          <div className="booth-league-info-table hscroll">
            <div className="booth-league-info-row booth-league-info-row--head">
              <span>Manager</span>
              <span>Email</span>
              <span>Status</span>
            </div>
            {members.map((member) => {
              const m = member.members;
              const hasEmail = !!m.email;
              const receiving = isReceiving(member);
              const isEditing = editingId === m.id;
              const isYou = m.email && m.email === user?.current?.email;

              return (
                <div className="booth-league-info-row" key={m.id}>
                  <span className="tname booth-league-info-name">
                    {m.name}
                    {isYou && (
                      <span className="booth-league-info-you">You</span>
                    )}
                  </span>

                  <span className="booth-league-info-email-cell">
                    {isEditing ? (
                      <input
                        type="text"
                        data-testid={`email-input-${m.id}`}
                        className="booth-field-input booth-field-input--sm"
                        placeholder="Add email"
                        value={draftEmail}
                        onChange={(event) => setDraftEmail(event.target.value)}
                      />
                    ) : m.email ? (
                      m.email
                    ) : (
                      <span className="booth-league-info-missing">
                        Not added
                      </span>
                    )}
                  </span>

                  <span className="booth-league-info-status-cell">
                    {isEditing ? (
                      <>
                        <input
                          type="checkbox"
                          className="booth-switch"
                          checked={draftSubscribed}
                          disabled={!draftEmail}
                          data-testid={`subscribed-switch-${m.id}`}
                          onChange={(event) =>
                            setDraftSubscribed(event.target.checked)
                          }
                        />
                        <button
                          type="button"
                          data-testid={`save-row-${m.id}`}
                          className="booth-link-underline"
                          onClick={() => saveEditing(m.id)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          data-testid={`cancel-row-${m.id}`}
                          className="booth-link-underline"
                          onClick={cancelEditing}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span
                          className={`booth-league-info-status ${
                            receiving ? "booth-league-info-status--active" : ""
                          }`}
                        >
                          <span
                            className="booth-league-info-status-dot"
                            aria-hidden="true"
                          />
                          {receiving
                            ? "Receiving"
                            : hasEmail
                              ? "Unsubscribed"
                              : "No email yet"}
                        </span>
                        <button
                          type="button"
                          data-testid={`edit-trigger-${m.id}`}
                          className="booth-link-underline booth-league-info-edit-trigger"
                          onClick={() => startEditing(member)}
                        >
                          {hasEmail ? "Edit" : "Add email"}
                          <EditIcon />
                        </button>
                        {hasEmail && (
                          <button
                            type="button"
                            data-testid={`reset-email-${m.id}`}
                            className="booth-link-underline booth-league-info-reset"
                            onClick={() =>
                              leagues.resetMemberEmail(league.id, m.id)
                            }
                          >
                            Reset
                          </button>
                        )}
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        <div className="booth-league-info-disable">
          {!confirmingDisable ? (
            <button
              type="button"
              className="booth-link-underline"
              onClick={() => {
                if (league.disabled_by_user) {
                  leagues.toggleLeagueDisabled(league.id, false);
                } else {
                  setConfirmingDisable(true);
                }
              }}
            >
              {league.disabled_by_user ? "Enable league" : "Disable league"}
            </button>
          ) : (
            <div className="booth-league-info-confirm">
              <p>
                Disable this league? This stops next week's writeup and all
                future emails until you turn it back on.
              </p>
              <div className="booth-modal-actions booth-modal-actions--group">
                <button
                  type="button"
                  className="booth-btn booth-btn--primary"
                  onClick={async (event) => {
                    event.preventDefault();
                    await leagues.toggleLeagueDisabled(league.id, true);
                    setConfirmingDisable(false);
                  }}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  className="booth-btn booth-btn--secondary"
                  onClick={(event) => {
                    event.preventDefault();
                    setConfirmingDisable(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeagueInfo;
